import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  from,
  HttpLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { ErrorBoundary } from 'react-error-boundary';
import { Bugfender } from '@bugfender/sdk';

import App from './App';
import './styles/tailwind.css';
import { environments } from './constants';
import bugfenderHelper from './services/bugfender';
import { validateEnvironment } from './utils/validateEnv';

validateEnvironment();

Bugfender.init({
  appKey: environments.BUGFENDER_APP_KEY,
  printToConsole: false,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
      );
      bugfenderHelper.error(`GraphQL Error: ${message}`);
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    bugfenderHelper.error(`Network Error: ${networkError.message}`);
  }
});

const httpLink = new HttpLink({
  uri: environments.REACT_APP_AUTH_API,
});

const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
});

function ErrorFallback({
  error,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="flex flex-row p-5 mt-20 max-w-fit">
      <div className="flex flex-col max-w-fit max-h-fit">
        <div className="text-left p-4">
          <h1 className="text-3xl">Oooopps...</h1>
          <h3 className="text-xl">Its not you its us</h3>
          <p className="text-left mb-10">
            An issue occurred, please click the refresh button below. If the
            issue persists please reach out to our support team:
            support@tripplo.freshdesk.com
          </p>
          <button
            className="bg-tripplo-200 text-white px-6 py-2 rounded"
            onClick={() => {
              window.location.href = '/login/sso-app/recover';
            }}
          >
            Refresh
          </button>
          <div className="text-red-400 mt-10 max-w-2xl">
            <h2>{error.name}</h2>
            <p>{error.message}</p>
            <pre className="text-xs overflow-auto">{error.stack}</pre>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <img
          className="max-h-64"
          src="https://re-temp.s3.eu-west-1.amazonaws.com/404+Page+Not+Found.png"
          alt="404"
        />
      </div>
    </div>
  );
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => {
        console.error('BOUNDARY-ERROR:', error);
        bugfenderHelper.error(error);
      }}
    >
      <ApolloProvider client={client}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ApolloProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
